import React, { useContext, useState, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { useQuery } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import useLearnerContext from '../hooks/useLearnerContext'
import languages, { getLanguageAcronym } from '../languages'
import flags from '../flags.js'
import { GET_DUE_ITEMS_QUERY, GET_DUE_COUNTS_QUERY } from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'

// Turn a due item into the flashcard shape the existing practice pipeline uses.
const toCard = (it) => ({
  sourceId: it.id,
  source: it.text,
  sourceLanguage: it.lang,
  sourceImageUrl: null,
  sourceImageDescription: null,
  translations: it.translationText
    ? [{ targetId: it.translationId, target: it.translationText, targetLanguage: it.translationLang || 'eng' }]
    : [],
})

// Three-letter code → display name: the profile knows the names of the
// languages you're learning; anything else comes from the code → name map.
const nameOfCode = (code, learning) => {
  const mine = (learning || []).find(l => l && l.name && (l.code === code || getLanguageAcronym(l.name) === code))
  if (mine) return mine.name
  return (languages && languages[code]) || String(code || '').toUpperCase()
}

const outOfDate = (msg) => /Cannot query field|Unknown type|Unknown argument/i.test(msg || '')

const chip = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#18181c', border: '1px solid #2a2a33', color: '#8a8a95', borderRadius: 999, padding: '0.35rem 0.75rem 0.35rem 0.6rem', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }
const chipOn = { borderColor: '#6ee7b7', color: '#e8e8ec', background: 'rgba(110,231,183,0.08)' }

// Review is per language: mixing Korean, Turkish and Portuguese cards in one
// run is a context switch every card. `initialLang` (from the dashboard) picks
// the language to open on; the chips switch between the rest.
//
// Which language an item belongs to is decided by the server: the language
// being learned, i.e. the non-native side of the pair. Practice is recorded
// on the source node — for an English speaker usually the English word — so
// grouping by that node's own language filed every card under "English".
export default function ReviewDueDialog({ onStartReview, initialLang }) {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const { native, learning } = useLearnerContext(username)
  const isOpen = dialogStates['dialogReviewDue']

  // Per-language counts drive the chips.
  const countsQ = useQuery(GET_DUE_COUNTS_QUERY, {
    variables: { username },
    skip: !isOpen || !username,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  const counts = useMemo(() => {
    const m = {}
    const rows = countsQ.data?.getDueCounts || []
    rows.forEach(c => { if (c && c.lang) m[c.lang] = c.count || 0 })
    return m
  }, [countsQ.data])

  const nativeCodes = useMemo(() => (Array.isArray(native) ? native : []).map(l => l && l.code).filter(Boolean), [native])
  const learningCodes = useMemo(
    () => (learning || []).map(l => (l && (l.code || getLanguageAcronym(l.name || ''))) || '').filter(Boolean),
    [learning]
  )
  // Chips: the languages you're learning first, then anything else with items
  // due — never a language you speak natively.
  const codes = useMemo(() => {
    const seen = new Set(nativeCodes)
    const out = []
    const all = [...learningCodes, ...Object.keys(counts)]
    all.forEach(c => { if (c && !seen.has(c)) { seen.add(c); out.push(c) } })
    return out
  }, [learningCodes, counts, nativeCodes])

  // The language on show. Reset on every open so the dashboard's choice wins;
  // otherwise the first language that actually has something due.
  const [picked, setPicked] = useState(null)
  useEffect(() => { if (isOpen) setPicked(null) }, [isOpen])
  const lang = picked
    || (initialLang && codes.includes(initialLang) ? initialLang : null)
    || codes.find(c => counts[c] > 0)
    || codes[0]
    || null

  // The list for that one language, fetched on its own so the 200 cap is per
  // language rather than shared across all of them.
  const itemsQ = useQuery(GET_DUE_ITEMS_QUERY, {
    variables: { username, lang, limit: 200 },
    skip: !isOpen || !username || !lang,
    fetchPolicy: 'network-only',
  })
  const visible = itemsQ.data?.getDueItems || []
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  const elsewhere = Math.max(0, total - (counts[lang] || 0))
  const langName = lang ? nameOfCode(lang, learning) : ''
  const error = countsQ.error || itemsQ.error

  if (!isOpen) return null
  const close = () => closeDialog('dialogReviewDue')

  const start = () => {
    if (visible.length === 0) return
    onStartReview(visible.map(toCard))
    close()
  }

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={close}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <button className={styles.closeBtn} onClick={close}><CloseIcon /></button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Spaced Repetition</div>
            <h2 className={styles.title}>Review due</h2>
          </div>
        </div>

        <div className={styles.container}>
          {countsQ.loading ? (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: '#8a8a95' }}>Checking what's due…</div>
          ) : error ? (
            <div style={{ color: '#f87171' }}>
              {outOfDate(error.message) ? 'The server needs updating before review can be split by language.' : error.message}
            </div>
          ) : (
            <>
              {codes.length > 1 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.9rem' }}>
                  {codes.map(c => {
                    const on = c === lang
                    return (
                      <button key={c} onClick={() => setPicked(c)} style={{ ...chip, ...(on ? chipOn : null) }} title={`${counts[c] || 0} due`}>
                        <span>{flags[c] || '🌐'}</span>{nameOfCode(c, learning)}
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', color: on ? '#6ee7b7' : '#8a8a95' }}>{counts[c] || 0}</span>
                      </button>
                    )
                  })}
                </div>
              )}

              {itemsQ.loading ? (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: '#8a8a95' }}>Loading {langName}…</div>
              ) : visible.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                  <div style={{ fontSize: '2.2rem' }}>✅</div>
                  <h3 style={{ margin: '0.5rem 0' }}>{langName ? `All caught up in ${langName}` : "You're all caught up"}</h3>
                  <p style={{ color: '#8a8a95', margin: 0 }}>
                    {elsewhere > 0
                      ? `${elsewhere} item${elsewhere === 1 ? ' is' : 's are'} due in your other languages — pick one above.`
                      : 'Nothing is due for review right now. New practice will schedule items back here automatically.'}
                  </p>
                </div>
              ) : (
                <>
                  <p style={{ color: '#e8e8ec', margin: '0 0 0.8rem' }}>
                    <strong style={{ color: '#6ee7b7', fontSize: '1.4rem' }}>{visible.length}</strong> {langName} item{visible.length === 1 ? '' : 's'} due — practise them before they fade.
                  </p>
                  <div style={{ maxHeight: 240, overflowY: 'auto', background: '#141418', border: '1px solid #2a2a33', borderRadius: 12, padding: '0.7rem 0.9rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {visible.map((it) => {
                      const acc = it.timesPracticed ? Math.round((it.timesCorrect / it.timesPracticed) * 100) : null
                      return (
                        <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.8rem', fontSize: '0.9rem' }}>
                          <span><span style={{ color: '#6ee7b7', fontWeight: 600 }}>{it.text}</span>{it.translationText ? <span style={{ color: '#8a8a95' }}> — {it.translationText}</span> : null}</span>
                          {acc != null && <span style={{ color: acc >= 70 ? '#8a8a95' : '#fbbf24', whiteSpace: 'nowrap' }}>{acc}%</span>}
                        </div>
                      )
                    })}
                  </div>
                  <button className={styles.input} style={{ cursor: 'pointer', width: '100%', padding: '0.75rem', background: '#34d399', color: '#06281d', fontWeight: 600, border: 'none', marginTop: '1rem' }} onClick={start}>
                    ▶ Start {langName} review ({visible.length})
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>, document.body
  )
}
